import { Ellipsis, PageButton, PaginationWrapper } from './Pagination.styles';

/** Props for the shared card-grid pager. */
type PaginationProps = {
  /** 1-indexed page currently being shown. */
  currentPage: number;
  /** Total number of pages; the pager renders nothing when there is only one. */
  totalPages: number;
  /** Fired with the 1-indexed page to navigate to. */
  onPageChange: (page: number) => void;
};

/**
 * Numbered pager with a sliding window of pages around the current one,
 * e.g. `Prev 1 … 4 5 [6] 7 8 … 12 Next`.
 *
 * Pure presentation — the caller owns the page state and slices its own
 * data; the pager only reports the requested page via `onPageChange`.
 */
export const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <PaginationWrapper>
      <PageButton
        type="button"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Prev
      </PageButton>
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2)
        .map((page, idx, arr) => {
          const prevPage = arr[idx - 1];
          const showEllipsis = prevPage !== undefined && page - prevPage > 1;
          return (
            <span key={page}>
              {showEllipsis ? <Ellipsis>&hellip;</Ellipsis> : null}
              <PageButton
                type="button"
                active={page === currentPage}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PageButton>
            </span>
          );
        })}
      <PageButton
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </PageButton>
    </PaginationWrapper>
  );
};
